/**
 * FloatingIncidentPanel - PROJ-066
 * Floating, resizable panel for incident editing
 * Follows IOPanel pattern from Workflows module
 * Supports Chilean ARCO+P compliance field prioritization
 */

import React, { useState, useCallback, useEffect, useRef } from 'react';
import Draggable, { DraggableData, DraggableEvent } from 'react-draggable';
import { GripVertical, X, Minus, Maximize2, Minimize2 } from 'lucide-react';
import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip';
import type { WindowState } from '@/contexts/WindowManagerContext';
import { FloatingPanelContent } from './FloatingPanelContent';

// Size constraints (following IOPanel pattern)
const DEFAULT_WIDTH = 560;
const DEFAULT_HEIGHT = 640;
const MIN_WIDTH = 450;
const MIN_HEIGHT = 400;
const MAX_WIDTH = 1000;
const MAX_HEIGHT = 900;

interface FloatingIncidentPanelProps {
  windowState: WindowState;
  onClose: () => void;
  onFocus: () => void;
  onMinimize: () => void;
  onPositionChange: (position: { x: number; y: number }) => void;
  onSizeChange: (size: { width: number; height: number }) => void;
  onIncidentUpdate: () => void;
}

export function FloatingIncidentPanel({
  windowState,
  onClose,
  onFocus,
  onMinimize,
  onPositionChange,
  onSizeChange,
  onIncidentUpdate,
}: FloatingIncidentPanelProps) {
  const panelRef = useRef<HTMLDivElement>(null);
  const [dimensions, setDimensions] = useState(windowState.size || { width: DEFAULT_WIDTH, height: DEFAULT_HEIGHT });
  const [isResizing, setIsResizing] = useState(false);
  const [isMaximized, setIsMaximized] = useState(false);
  const [preMaximizeState, setPreMaximizeState] = useState<{
    position: { x: number; y: number };
    size: { width: number; height: number };
  } | null>(null);
  const startPosRef = useRef({ x: 0, y: 0, width: 0, height: 0 });

  // Sync dimensions from props
  useEffect(() => {
    if (windowState.size) {
      setDimensions(windowState.size);
    }
  }, [windowState.size]);

  // Handle resize mouse down
  const handleResizeMouseDown = useCallback((e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsResizing(true);
    startPosRef.current = {
      x: e.clientX,
      y: e.clientY,
      width: dimensions.width,
      height: dimensions.height,
    };
  }, [dimensions]);

  // Handle resize mouse move
  useEffect(() => {
    if (!isResizing) return;

    const handleMouseMove = (e: globalThis.MouseEvent) => {
      const deltaX = e.clientX - startPosRef.current.x;
      const deltaY = e.clientY - startPosRef.current.y;

      const newWidth = Math.min(MAX_WIDTH, Math.max(MIN_WIDTH, startPosRef.current.width + deltaX));
      const newHeight = Math.min(MAX_HEIGHT, Math.max(MIN_HEIGHT, startPosRef.current.height + deltaY));

      setDimensions({ width: newWidth, height: newHeight });
    };

    const handleMouseUp = () => {
      setIsResizing(false);
      onSizeChange(dimensions);
    };

    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseup', handleMouseUp);

    return () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
    };
  }, [isResizing, dimensions, onSizeChange]);

  // Handle drag start - focus panel
  const handleDragStart = useCallback(() => {
    onFocus();
  }, [onFocus]);

  // Handle drag stop - update position
  const handleDragStop = useCallback((_e: DraggableEvent, data: DraggableData) => {
    onPositionChange({ x: data.x, y: data.y });
  }, [onPositionChange]);

  // Handle maximize/restore
  const handleMaximize = useCallback(() => {
    if (isMaximized) {
      // Restore
      if (preMaximizeState) {
        setDimensions(preMaximizeState.size);
        onPositionChange(preMaximizeState.position);
        onSizeChange(preMaximizeState.size);
      }
      setIsMaximized(false);
    } else {
      // Maximize
      setPreMaximizeState({
        position: windowState.position,
        size: dimensions,
      });
      const maxSize = {
        width: window.innerWidth - 100,
        height: window.innerHeight - 100,
      };
      setDimensions(maxSize);
      onPositionChange({ x: 50, y: 50 });
      onSizeChange(maxSize);
      setIsMaximized(true);
    }
  }, [isMaximized, preMaximizeState, windowState.position, dimensions, onPositionChange, onSizeChange]);

  // Handle panel click to focus
  const handlePanelMouseDown = useCallback(() => {
    if (!windowState.isFocused) {
      onFocus();
    }
  }, [windowState.isFocused, onFocus]);

  // If minimized, don't render the panel
  if (windowState.isMinimized) {
    return null;
  }

  return (
    <Draggable
      nodeRef={panelRef}
      handle=".floating-panel-drag-handle"
      cancel=".no-drag, input, textarea, button:not(.drag-handle-btn), select, [role='combobox'], [data-radix-scroll-area-viewport]"
      bounds="body"
      position={windowState.position}
      onStart={handleDragStart}
      onStop={handleDragStop}
    >
      <div
        ref={panelRef}
        className={cn(
          'fixed flex flex-col bg-background border rounded-lg shadow-xl overflow-hidden',
          'transition-shadow duration-200',
          windowState.isFocused && 'ring-2 ring-primary/50 shadow-2xl',
          isResizing && 'select-none'
        )}
        style={{
          width: dimensions.width,
          height: dimensions.height,
          zIndex: windowState.zIndex,
        }}
        onMouseDown={handlePanelMouseDown}
      >
        {/* Header with drag handle and window controls */}
        <div
          className={cn(
            'floating-panel-drag-handle',
            'flex items-center justify-between px-4 py-2',
            'bg-muted/50 border-b cursor-move',
            'select-none'
          )}
        >
          <div className="flex items-center gap-2 min-w-0 flex-1">
            <GripVertical className="h-4 w-4 text-muted-foreground shrink-0" />
            <span className="text-sm font-medium truncate">
              {windowState.incident.incidentNumber
                ? `Incidente ${windowState.incident.incidentNumber}`
                : windowState.incident.title || 'Nuevo Incidente'
              }
            </span>
            {windowState.incident.type && (
              <span className="text-xs px-2 py-0.5 bg-primary/10 text-primary rounded-full shrink-0">
                {windowState.incident.type.replace(/_/g, ' ')}
              </span>
            )}
          </div>

          <div className="flex items-center gap-1 shrink-0 ml-2">
            {/* Minimize button */}
            <Tooltip>
              <TooltipTrigger asChild>
                <Button
                  variant="ghost"
                  size="icon"
                  className="h-7 w-7 drag-handle-btn no-drag"
                  onClick={(e) => {
                    e.stopPropagation();
                    onMinimize();
                  }}
                >
                  <Minus className="h-4 w-4" />
                </Button>
              </TooltipTrigger>
              <TooltipContent>Minimizar (Ctrl+M)</TooltipContent>
            </Tooltip>

            {/* Maximize/Restore button */}
            <Tooltip>
              <TooltipTrigger asChild>
                <Button
                  variant="ghost"
                  size="icon"
                  className="h-7 w-7 drag-handle-btn no-drag"
                  onClick={(e) => {
                    e.stopPropagation();
                    handleMaximize();
                  }}
                >
                  {isMaximized ? (
                    <Minimize2 className="h-4 w-4" />
                  ) : (
                    <Maximize2 className="h-4 w-4" />
                  )}
                </Button>
              </TooltipTrigger>
              <TooltipContent>{isMaximized ? 'Restaurar' : 'Maximizar'}</TooltipContent>
            </Tooltip>

            {/* Close button */}
            <Tooltip>
              <TooltipTrigger asChild>
                <Button
                  variant="ghost"
                  size="icon"
                  className="h-7 w-7 hover:bg-destructive hover:text-destructive-foreground drag-handle-btn no-drag"
                  onClick={(e) => {
                    e.stopPropagation();
                    onClose();
                  }}
                >
                  <X className="h-4 w-4" />
                </Button>
              </TooltipTrigger>
              <TooltipContent>Cerrar (Ctrl+W)</TooltipContent>
            </Tooltip>
          </div>
        </div>

        {/* Panel content */}
        <FloatingPanelContent
          incident={windowState.incident}
          onClose={onClose}
          onIncidentUpdate={onIncidentUpdate}
        />

        {/* Resize handle (bottom-right corner) */}
        <div
          className={cn(
            'absolute bottom-1 right-1',
            'flex items-center justify-center',
            'w-6 h-6 rounded-md cursor-nwse-resize',
            'bg-muted/80 hover:bg-muted border border-border/50 hover:border-border',
            'text-muted-foreground hover:text-foreground',
            'shadow-sm hover:shadow transition-all',
            isResizing && 'bg-primary/20 border-primary'
          )}
          onMouseDown={handleResizeMouseDown}
          title="Arrastrar para redimensionar"
        >
          <GripVertical className="h-3.5 w-3.5 rotate-45" />
        </div>
      </div>
    </Draggable>
  );
}

export default FloatingIncidentPanel;
